import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../../design-system/tokens';

interface FortuneServiceCardProps {
  title: string;
  description: string;
  image: string;
  imageHeight?: number;
  onClick?: () => void;
  'data-name'?: string;
}

const Card = styled.div`
  background-color: ${tokens.colors.neutral[50]};
  border-radius: ${tokens.borderRadius.lg};
  padding: ${tokens.spacing[6]} ${tokens.spacing[8]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.spacing[6]};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.easeOut};
  overflow: hidden;
  height: 140px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${tokens.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[5]} ${tokens.spacing[6]};
    gap: ${tokens.spacing[5]};
    height: 120px;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[4]} ${tokens.spacing[5]};
    gap: ${tokens.spacing[4]};
    height: 110px;
  }
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[2]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    gap: ${tokens.spacing[1]};
  }
`;

const Title = styled.h3`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.bold};
  line-height: 1.4;
  color: ${tokens.colors.neutral[800]};
  margin: 0;
  letter-spacing: -0.05em;
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.lg};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.base};
  }
`;

const Description = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.light};
  line-height: 1.5;
  color: ${tokens.colors.neutral[800]};
  margin: 0;
  letter-spacing: -0.05em;
  white-space: pre-line;
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.xs};
    line-height: 1.4;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
    line-height: 1.3;
  }
`;

const ImageContainer = styled.div<{ $height?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    height: ${props => props.$height || 100}px;
    width: auto;
    object-fit: contain;
  }

  @media (max-width: ${tokens.breakpoints.md}) {
    img {
      height: ${props => ((props.$height || 100) * 0.85)}px;
    }
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    img {
      height: ${props => ((props.$height || 100) * 0.65)}px;
    }
  }
`;

export const FortuneServiceCard: React.FC<FortuneServiceCardProps> = ({
  title,
  description,
  image,
  imageHeight,
  onClick,
  ...props
}) => {
  return (
    <Card onClick={onClick} {...props}>
      <TextContainer>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </TextContainer>
      <ImageContainer $height={imageHeight}>
        <img src={image} alt={title} />
      </ImageContainer>
    </Card>
  );
};